import React, { useEffect, useState } from 'react';

import { Box, HStack, Stack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { DateRange } from 'react-day-picker';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { customTheme } from 'theme';

import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { CustomDayPicker } from 'components/DataEntry/Date';
import {
  BreadcrumbListOptions,
  Card,
  formatBytes,
  getIconColor,
  IconTypes,
  StackedHeader,
  WithResourceIcon,
} from 'components/index';
import { Breadcrumbs } from 'components/Navigation/Breadcrumbs';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';
import { selectIncidentResponse } from 'containers/Visibility/IncidentResponse/selectors';
import { actions } from 'containers/Visibility/IncidentResponse/slice';
import { useSearchParams } from 'hooks/useSearchParams';
import { limitedString } from 'utils/string';

import { CloudEventsSummaryButton } from '../../CloudEventsSummaryButton';
import { renderCloudEventsInsights, renderRequestCount } from '../../utils';
import { CloudEventsResourceSummary } from '../Summary/CloudEventsResourceSummary';

export const AggregatedCloudEventsForResource = () => {
  const params = useSearchParams<{
    tab: string;
    resource_type: string;
    date: string;
    account: string;
    contexts: string;
    groupName: string;
  }>();
  const { group_id, resource_id } = useParams();
  const { getResourceAlias } = useResourceType();
  const navigate = useNavigate();
  const { aggregateResourceEvents } = useSelector(selectIncidentResponse);
  const { resourceInsight } = useSelector(selectSharedState);
  const dispatch = useDispatch();
  const { accountMapping } = useCloudAccountId();
  const [showSummary, setShowSummary] = useState(false);
  const [summaryRange, setSummaryRange] = useState<DateRange>();

  useEffect(() => {
    dispatch(
      sharedStateActions.getResourceInsight({
        q: {
          insightV2Request: {
            resource_type: params.resource_type,
            resource_ids: [resource_id!],
          },
        },
        onSuccess: response => {
          const insight = response?.items?.[0];
          if (!insight) return;
          const cloudAccountId = insight.account
            ? accountMapping[insight.account]?.cloud_account_id
            : undefined;

          dispatch(
            actions.getAggregateResourceEvents({
              q: {
                body: {
                  account_id: cloudAccountId ?? undefined,
                  resource_type: params.resource_type,
                  start_date: dayjs(params.date).startOf('day').utc().format(),
                  resource_arn: insight.arn,
                  end_date: dayjs(params.date).endOf('day').utc().format(),
                },
              },
            }),
          );
        },
      }),
    );
  }, [
    accountMapping,
    dispatch,
    resource_id,
    params.date,
    params.resource_type,
  ]);

  const breadcrumbList: BreadcrumbListOptions[] = [
    {
      label: 'Data',
      onClick: () => navigate('/visibility/activity?tab=Data'),
    },
    {
      label: 'Activity Group - ' + params?.groupName ?? '-',
      onClick: () =>
        navigate(
          '/visibility/activity/group/' +
            group_id +
            '?tab=Data&resource_type=' +
            params.resource_type +
            '&groupName=' +
            params.groupName +
            '&date=' +
            params.date +
            '&insightCategories=' +
            params.insightCategories +
            (params.contexts
              ? '&contexts=' + encodeURIComponent(params.contexts)
              : ''),
        ),
    },
    { label: resource_id + ' - Aggregated' },
  ];

  const columns: TableColumnProps[] = [
    {
      header: (
        <Box pl={10} ml={1}>
          Time Period
        </Box>
      ),
      render: ({ row }) => {
        const time =
          dayjs(row.hour).utc().local().format('HH:mm:ss') +
          ' - ' +
          dayjs(row.hour).add(1, 'h').format('HH:mm:ss');
        return (
          <WithResourceIcon
            resourceType={IconTypes.Clock}
            tooltip={time}
            color={getIconColor(IconTypes.Aws)}
          >
            {time}
          </WithResourceIcon>
        );
      },
    },
    {
      header: 'Total Requests',
      render: ({ row }) =>
        renderRequestCount(row?.total_requests, customTheme.colors.blue['300']),
    },
    {
      header: 'Failed Requests',
      render: ({ row }) =>
        renderRequestCount(row?.failed_requests, customTheme.colors.critical),
    },
    {
      header: 'Objects Accessed',
      render: ({ row }) => formatNumber(row?.objects_accessed, 1),
    },
    {
      header: 'Data Accessed',
      render: ({ row }) => formatBytes(row?.data_accessed_bytes),
    },
    {
      header: 'Users',
      accessor: 'users',
      render: ({ value }) => value,
    },
    // {
    //   header: 'Apps',
    //   accessor: 'apps',
    //   render: ({ value }) => value,
    // },
    {
      header: 'Roles',
      accessor: 'roles',
      render: ({ value }) => value,
    },
    {
      header: 'Insights',
      render: renderCloudEventsInsights,
    },
  ];

  return (
    <Card>
      <Stack w="full" h="full" spacing={4}>
        <Breadcrumbs list={breadcrumbList} />
        <HStack w="full" justify="space-between">
          <Box flex={1}>
            <StackedHeader
              upper={'Cloud Events for ' + resource_id}
              lower={getResourceAlias(params.resource_type)}
              type={params.resource_type}
              color={getIconColor('aws')}
              useCustomColor
              border
              borderColor={customTheme.colors.yellow['300']}
            />
          </Box>
          {/*<CloudEventsSummaryButton
            isActive={showSummary}
            onHide={() => setShowSummary(false)}
            tooltip={
              'Select summary range for ' + limitedString(resource_id, 16)
            }
            selectedRange={summaryRange}
            onSelect={range => {
              setSummaryRange(range);
              setShowSummary(true);
            }}
          />*/}
        </HStack>
        {showSummary && summaryRange && (
          <CloudEventsResourceSummary
            accountId={
              accountMapping[resourceInsight.data?.account]?.cloud_account_id
            }
            dateRange={summaryRange}
            resourceArn={resourceInsight.data?.arn}
            resourceType={params.resource_type}
          />
        )}
        <BorderedBox flex={1} w="full" h="full" p={4} minH="500px">
          <Stack w="full" h="full" spacing={4}>
            <HStack>
              <FilterLabelWrapper>VIEW BY</FilterLabelWrapper>
              <Box pl={2} color={customTheme.colors.gray['300']}>
                Date
              </Box>
              <Box>
                <CustomDayPicker
                  isDisabled
                  dateProps={{
                    mode: 'single' as any,
                    selected: params.date,
                  }}
                />
              </Box>
              {/*<FilterLabelWrapper>FILTER BY</FilterLabelWrapper>*/}
            </HStack>
            <Box flex={1} w="full" h="full">
              <Table
                columns={columns}
                data={aggregateResourceEvents.data}
                isLoading={
                  aggregateResourceEvents.isLoading || resourceInsight.isLoading
                }
                onRowClick={row =>
                  navigate(
                    '/visibility/activity/group/' +
                      group_id +
                      '/resource/' +
                      resource_id +
                      '/events/?tab=Data&groupName=' +
                      params.groupName +
                      '&resource_type=' +
                      params.resource_type +
                      '&date=' +
                      params.date +
                      '&hour=' +
                      row.hour +
                      '&total=' +
                      row.total_requests +
                      '&insightCategories=' +
                      params.insightCategories +
                      (params.contexts
                        ? '&contexts=' + encodeURIComponent(params.contexts)
                        : ''),
                  )
                }
              />
            </Box>
          </Stack>
        </BorderedBox>
      </Stack>
    </Card>
  );
};
